import { Box, Tooltip } from "@mui/material";
import type { KeyboardEvent } from "react";
import { useCardLayout } from "./CardLayoutContext";
import { CARD_RADIUS, DRAG_HANDLE_CLASS } from "./grid";
import type { MoveDirection } from "./keyboard";

const ARROW_DIRECTIONS: Record<string, MoveDirection> = {
    ArrowLeft: "left",
    ArrowRight: "right",
    ArrowUp: "up",
    ArrowDown: "down",
};

/** The grip's dots: a triangle pointing into the card's top-left corner. */
const GRIP_DOTS: readonly (readonly [number, number])[] = [
    [0, 0],
    [4, 0],
    [8, 0],
    [12, 0],
    [0, 4],
    [4, 4],
    [8, 4],
    [0, 8],
    [4, 8],
    [0, 12],
];

const GripDots = () => (
    <svg aria-hidden width={14} height={14} viewBox="0 0 14 14">
        {GRIP_DOTS.map(([x, y]) => (
            <rect
                key={`${x}-${y}`}
                x={x}
                y={y}
                width={2}
                height={2}
                fill="currentColor"
            />
        ))}
    </svg>
);

/**
 * The corner grip a card is dragged by while the dashboard is being arranged,
 * which also moves the card with the arrow keys and resizes it with Shift and
 * the arrow keys. Renders nothing when the dashboard cannot be arranged.
 */
export const LayoutGrip = ({
    objectId,
    label,
    tooltip = "Drag to move. Arrow keys move, Shift and arrow keys resize.",
}: {
    objectId: string;
    label: string;
    tooltip?: string;
}) => {
    const cardLayout = useCardLayout();
    const layoutStore = cardLayout?.layoutStore;
    const canArrange = cardLayout?.canArrange ?? false;

    if (!canArrange) return null;

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        const direction = ARROW_DIRECTIONS[event.key];
        if (!layoutStore || !direction) return;

        event.preventDefault();
        event.stopPropagation();
        layoutStore.applyKeyboardOperation({
            objectId,
            direction,
            resize: event.shiftKey,
        });

        // Cards render in visual order, so a move can make React reposition
        // this card's node, which drops focus. Put it back on the grip.
        const grip = event.currentTarget;
        window.requestAnimationFrame(() => {
            if (grip.isConnected && document.activeElement !== grip) {
                grip.focus();
            }
        });
    };

    return (
        <Tooltip
            title={tooltip}
            // Popper flags a tooltip whose grip has scrolled out of view;
            // without this it stays floating over the dashboard.
            PopperProps={{
                sx: {
                    "&[data-popper-reference-hidden]": {
                        visibility: "hidden",
                        pointerEvents: "none",
                    },
                },
            }}
        >
            <Box
                aria-label={label}
                className={DRAG_HANDLE_CLASS}
                role="button"
                tabIndex={0}
                onKeyDown={handleKeyDown}
                // The hit area is the corner square; the dots sit inside it,
                // tucked into the card's corner. The grid hides the grip until
                // the card is hovered.
                sx={(theme) => ({
                    position: "absolute",
                    top: 0,
                    left: 0,
                    zIndex: 7,
                    width: 24,
                    height: 24,
                    padding: "5px",
                    boxSizing: "border-box",
                    lineHeight: 0,
                    borderTopLeftRadius: CARD_RADIUS,
                    cursor: "grab",
                    color: theme.palette.text.secondary,
                    "&:hover, &:focus-visible, &:active": {
                        color: theme.palette.primary.main,
                    },
                    "&:active": { cursor: "grabbing" },
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: -2,
                    },
                })}
            >
                <GripDots />
            </Box>
        </Tooltip>
    );
};
